import { Check, X } from "lucide-react";
import { useMemo } from "react";
import { toJS } from "mobx";
import { useManageUserPageData } from "../hooks";
import { Minus } from "lucide-react";
import {
  EXCLUDED_PERMISSIONS_IDS,
  RESTRICTED_ACTIONS,
} from "@/helpers/permissionConstants";
import { getRestrictedActions } from "@/helpers";
import { useTran } from "@/hooks";
import { MODULE_TABLE_HEADERS } from "../utils/roles-permissions";
import { cn } from "@/lib/utils";

const renderPermissionFlag = (value) => (
  <span className="inline-flex items-center justify-center">
    {value ? (
      <Check className="h-3.5 w-3.5 text-emerald-600" />
    ) : (
      <X className="h-3.5 w-3.5 text-red-600" />
    )}
  </span>
);

const formatPermissionModules = (modules = []) => {
  const result = [];

  toJS(modules).forEach((module) => {
    if (module.subModule?.length) {
      module.subModule.forEach((sub) => {
        result.push({
          key: sub.key,
          label: `${module.title} / ${sub.title}`,
        });
      });
    } else {
      result.push({
        key: module.key,
        label: module.title,
      });
    }
  });

  return result;
};

const buildPermissionTableRows = (modules = [], permissions = []) => {
  const map = {};

  // init all rows
  modules.forEach(({ key }) => {
    map[key] = {
      create: false,
      update: false,
      view: false,
      delete: false,
    };
  });

  permissions.forEach((perm) => {
    if (typeof perm !== "string") return;

    const [module, action] = perm.split(":");
    if (!map[module]) return;

    if (map[module][action] !== undefined) {
      map[module][action] = true;
    }
  });

  return modules
    .map(({ key, label }) => ({
      module: label,
      actions: map[key],
      key,
    }))
    .filter(({ actions }) => Object.values(actions).some(Boolean));
};

const PermissionViewsTable = ({ user = null, permissions = [] }) => {
  const { t } = useTran();
  const { permissionModules } = useManageUserPageData({ userId: user?.id });
  const MODULES = useMemo(() => {
    const modules = formatPermissionModules(permissionModules);
    // Remove unwanted IDs from the modules list
    return modules.filter((mod) => !EXCLUDED_PERMISSIONS_IDS.includes(mod.key));
  }, [permissionModules]);

  const permissionRows = useMemo(
    () => buildPermissionTableRows(MODULES, permissions),
    [MODULES, permissions],
  );

  if (!permissionRows.length) {
    return <span className="text-sm text-foreground">No permissions data</span>;
  }

  return (
    <div className="overflow-x-auto rounded-radius border border-border">
      <div className="min-w-[420px]">
        {/* Header */}
        <div className="grid grid-cols-6 bg-muted/40 px-3 py-2 text-[14px] font-semibold tracking-wide text-muted-foreground text-primary">
          {MODULE_TABLE_HEADERS.map((header) => (
            <span
              key={header}
              className={cn(
                "font-medium",
                header === "Module" ? "col-span-2" : "text-center",
              )}
            >
              {t(header)}
            </span>
          ))}
        </div>

        {/* Rows */}
        {permissionRows.map((row) => {
          const restricted = new Set(
            getRestrictedActions(row.key, user?.role, false) || [],
          );

          const renderAction = (action) => (
            <span className="text-center">
              {restricted.has(action) ? (
                <Minus size={10} className="mx-auto text-muted-foreground" />
              ) : (
                renderPermissionFlag(row.actions[action])
              )}
            </span>
          );

          return (
            <div
              key={row.module}
              className="grid grid-cols-6 items-center border-t border-border px-2 py-1 text-xs"
            >
              <span className="col-span-2 font-medium">{row.module}</span>
              {renderAction("view")}
              {renderAction("create")}
              {renderAction("update")}
              {renderAction("delete")}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionViewsTable;
